import { keyframes } from "@mui/material/styles";

/**
 * What a traverser stop looks like — the one field "Prev / Next" (or clicking an issue) just took
 * the reader to — wherever that field is drawn: a table row, or the issue above the table when the
 * stop has no row to light, takes one tint and one pulse. A stop lights one of the two, never both —
 * see `proposal-issue-list` and `traverser-stop-scope`.
 *
 * They used to be drawn four different ways. The issue box pulsed a ring outside itself; the draft
 * fee schedule table pulsed a thin band inside its cells that faded almost to nothing; the other
 * tables pulsed the colour of a 2px border. None of them held a tint, so at the bottom of each beat
 * the row read as unmarked while the issue beside it still looked lit.
 */

const STOP_RGB = "21, 101, 192";
const EDGE_LOW = `rgba(${STOP_RGB}, 0.28)`;
const EDGE_HIGH = `rgba(${STOP_RGB}, 0.75)`;
const EDGE_WIDTH = "2px";
const TINT = `rgba(${STOP_RGB}, 0.08)`;
const TIMING = "1.5s ease-in-out infinite";

/**
 * The steady tint, as an image so it layers over whatever background the element already has — a
 * severity colour, a pending-change tint, a resolved-green cell — instead of replacing it.
 */
export const TRAVERSER_STOP_TINT = `linear-gradient(${TINT}, ${TINT})`;

/** For an element that draws its own `border-color` edge — a row whose cells inherit it. */
const borderPulse = keyframes`
  0%, 100% { border-color: ${EDGE_LOW}; }
  50% { border-color: ${EDGE_HIGH}; }
`;

function insetEdges(color: string, sides: { top: boolean; right: boolean; bottom: boolean; left: boolean }) {
  return [
    sides.top && `inset 0 ${EDGE_WIDTH} 0 ${color}`,
    sides.right && `inset -${EDGE_WIDTH} 0 0 ${color}`,
    sides.bottom && `inset 0 -${EDGE_WIDTH} 0 ${color}`,
    sides.left && `inset ${EDGE_WIDTH} 0 0 ${color}`,
  ]
    .filter(Boolean)
    .join(", ");
}

function insetPulse(sides: { top: boolean; right: boolean; bottom: boolean; left: boolean }) {
  return keyframes`
    0%, 100% { box-shadow: ${insetEdges(EDGE_LOW, sides)}; }
    50% { box-shadow: ${insetEdges(EDGE_HIGH, sides)}; }
  `;
}

/** A box drawn as one element. Inset, so an `overflow: hidden` parent cannot clip it. */
const boxPulse = insetPulse({ top: true, right: true, bottom: true, left: true });
/**
 * A table row drawn on its cells, since a cell paints above its row. Only the end cells take a side
 * edge: a ring on every cell would draw a line at each column boundary.
 */
const rowCellPulse = insetPulse({ top: true, right: false, bottom: true, left: false });
const rowStartCellPulse = insetPulse({ top: true, right: false, bottom: true, left: true });
const rowEndCellPulse = insetPulse({ top: true, right: true, bottom: true, left: false });

/** The `@keyframes` names of every traverser stop pulse — what {@link syncTraverserStopPulses} looks for. */
export const TRAVERSER_STOP_PULSE_NAMES: ReadonlySet<string> = new Set(
  [borderPulse, boxPulse, rowCellPulse, rowStartCellPulse, rowEndCellPulse].map((pulse) => pulse.name),
);

/** A traverser stop drawn on one element: the tint, and an inset ring that pulses. */
export const traverserStopBoxStyles = {
  animation: `${boxPulse} ${TIMING}`,
  backgroundImage: TRAVERSER_STOP_TINT,
};

/**
 * A traverser stop on a table row whose cells draw its edges with `border-color: inherit`: the
 * pulse goes on the row, and each surrounded cell takes the tint with {@link traverserStopCellTint}.
 */
export const traverserStopRowBorderPulse = `${borderPulse} ${TIMING}`;

/** The tint for a cell inside a traverser stop's row. */
export const traverserStopCellTint = { backgroundImage: TRAVERSER_STOP_TINT };

/**
 * A traverser stop on a table row with no border of its own to pulse: every cell takes the tint and
 * an inset band, and the first and last cells close it at the sides.
 *
 * A pinned cell takes both on its `::before` instead. Material React Table paints a pinned cell's
 * backdrop there — an opaque layer at `z-index: -1` inside the sticky cell's own stacking context —
 * and that layer sits above the cell's background and inset shadow, so on the cell itself the tint
 * and band are drawn and then covered: the row's trash-can column stayed white and unbanded. On the
 * layer, the band stands in for the pinned column's separator shadow on that one row.
 */
export const traverserStopRowCellStyles = {
  "& > td": { animation: `${rowCellPulse} ${TIMING}`, backgroundImage: TRAVERSER_STOP_TINT },
  "& > td:first-of-type": { animation: `${rowStartCellPulse} ${TIMING}` },
  "& > td:last-of-type": { animation: `${rowEndCellPulse} ${TIMING}` },
  '& > td[data-pinned="true"]::before': {
    animation: `${rowCellPulse} ${TIMING}`,
    backgroundImage: TRAVERSER_STOP_TINT,
  },
  '& > td[data-pinned="true"]:first-of-type::before': { animation: `${rowStartCellPulse} ${TIMING}` },
  '& > td[data-pinned="true"]:last-of-type::before': { animation: `${rowEndCellPulse} ${TIMING}` },
};

/**
 * Puts every traverser stop pulse on the page on one beat.
 *
 * A CSS animation starts when its element takes the style, so two stop pulses — drawn by different
 * components, and one of them possibly re-rendered later — can drift apart and flash in turn. Giving
 * each the same start time on the document timeline keeps them in phase however they were mounted.
 */
export function syncTraverserStopPulses(): void {
  if (typeof document === "undefined" || typeof document.getAnimations !== "function") return;
  for (const animation of document.getAnimations()) {
    if (isCssAnimation(animation) && TRAVERSER_STOP_PULSE_NAMES.has(animation.animationName)) {
      animation.startTime = 0;
    }
  }
}

function isCssAnimation(animation: Animation): animation is CSSAnimation {
  return "animationName" in animation;
}
